import type { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { ListTenantActivationsQueryParams } from "../../client-messages/activations";

/** Which slice of a tenant's activations a list shows. All absent spans the tenant's whole history. */
export interface TenantActivationFilters {
  readonly site?: SiteId;
  readonly camera?: AssetCameraKey;
  /** Inclusive UTC instant, against the activation's `createdDateTime`. */
  readonly since?: string;
  /** Inclusive UTC instant, against the activation's `createdDateTime`. */
  readonly until?: string;
}

export type ParsedTenantActivationFilters =
  | { readonly ok: true; readonly filters: TenantActivationFilters }
  | { readonly ok: false; readonly problem: string };

/** Filters as they appear in a page URL. `from` / `to` are calendar days, `YYYY-MM-DD`. */
export interface RawActivationFilters {
  readonly site?: string | undefined;
  readonly camera?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
}

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A calendar day in the viewer's own zone, as the instant it starts or ends.
 *
 * Resolved here rather than by the API because only the viewer knows which
 * zone "1 September" means — the Lambda runs in UTC, so a bare day sent there
 * would drop late-evening activations west of UTC and pull in the next morning
 * east of it. Same reasoning as the clip compilation list's date bounds.
 */
function dayBoundary(day: string, edge: "start" | "end"): string | undefined {
  const match = DAY.exec(day);
  if (!match) return undefined;
  const [, y, m, d] = match.map(Number);
  if (y === undefined || m === undefined || d === undefined) return undefined;
  const date = edge === "start" ? new Date(y, m - 1, d) : new Date(y, m - 1, d, 23, 59, 59, 999);
  // Rejects days that do not exist rather than letting `Date` roll 31 Feb into March.
  if (date.getMonth() !== m - 1 || date.getDate() !== d) return undefined;
  return date.toISOString();
}

const blank = (value: string | undefined): string | undefined => (value === "" ? undefined : value);

type DayRange =
  | { readonly ok: true; readonly since: string | undefined; readonly until: string | undefined }
  | { readonly ok: false; readonly problem: string };

function parseDayRange(from: string | undefined, to: string | undefined): DayRange {
  const since = from === undefined ? undefined : dayBoundary(from, "start");
  const until = to === undefined ? undefined : dayBoundary(to, "end");
  if (from !== undefined && since === undefined) {
    return { ok: false, problem: `The start date "${from}" in this link is not a valid day.` };
  }
  if (to !== undefined && until === undefined) {
    return { ok: false, problem: `The end date "${to}" in this link is not a valid day.` };
  }
  if (since !== undefined && until !== undefined && since > until) {
    return { ok: false, problem: "The date range in this link ends before it starts." };
  }
  return { ok: true, since, until };
}

/**
 * Reads list filters from raw URL params.
 *
 * Validated with the endpoint's own query schema, so the page refuses exactly
 * what the API would refuse. A hand-edited or stale link that fails is reported
 * as a problem rather than dropped: dropping `camera` would silently widen the
 * list to the whole site, and sending it would come back as an empty list or a
 * 400 — both of which read as "no activations here".
 */
export function parseTenantActivationFilters(raw: RawActivationFilters): ParsedTenantActivationFilters {
  const site = blank(raw.site);
  const camera = blank(raw.camera);
  const from = blank(raw.from);
  const to = blank(raw.to);

  const window = parseDayRange(from, to);
  if (!window.ok) return window;
  const { since, until } = window;

  const result = ListTenantActivationsQueryParams.safeDecode({
    ...(site !== undefined && { site }),
    ...(camera !== undefined && { camera }),
  });

  if (!result.success) {
    const issue = result.error.issues[0];
    const field = issue?.path.join(".") ?? "filter";
    return {
      ok: false,
      problem: `The ${field} filter in this link is not valid: ${issue?.message ?? "unknown error"}`,
    };
  }

  return {
    ok: true,
    filters: {
      ...(result.data.site !== undefined && { site: result.data.site }),
      ...(result.data.camera !== undefined && { camera: result.data.camera }),
      ...(since !== undefined && { since }),
      ...(until !== undefined && { until }),
    },
  };
}

/**
 * Identity of a filtered list, for anything that must start over when the
 * filters change — a cursor is only meaningful to the query that issued it.
 */
export function activationFiltersKey(filters: {
  readonly site?: string;
  readonly camera?: string;
  readonly since?: string;
  readonly until?: string;
}): string {
  return [filters.site, filters.camera, filters.since, filters.until].map((v) => v ?? "").join("|");
}

/** How far back the fleet list reaches when the link names no start day. */
export const FLEET_DEFAULT_DAYS = 365;

/** Which slice of the fleet's activations a list shows. Always bounded below — see `ListFleetActivationsQueryParams`. */
export interface FleetActivationFilters {
  /** Inclusive instant, against the activation's `createdDateTime`. */
  readonly since: string;
  /** Inclusive instant; absent reads up to now. */
  readonly until?: string;
}

export type ParsedFleetActivationFilters =
  | {
      readonly ok: true;
      readonly filters: FleetActivationFilters;
      /** The days actually applied, defaulted start included, for the picker to show. */
      readonly days: { readonly from: string; readonly to?: string };
    }
  | { readonly ok: false; readonly problem: string };

const toDay = (date: Date): string =>
  `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/**
 * Reads fleet list filters — a day range only — from raw URL params.
 *
 * A link naming no start shows the {@link FLEET_DEFAULT_DAYS} days up to its
 * end, or up to `today`. The default is a day rather than an instant so the
 * query, and the cursor it issues, stays the same across renders.
 */
export function parseFleetActivationFilters(
  raw: Pick<RawActivationFilters, "from" | "to">,
  today: Date,
): ParsedFleetActivationFilters {
  const to = blank(raw.to);
  const window = parseDayRange(blank(raw.from), to);
  if (!window.ok) return window;

  const end = window.until === undefined ? today : new Date(window.until);
  const from =
    blank(raw.from) ?? toDay(new Date(end.getFullYear(), end.getMonth(), end.getDate() - (FLEET_DEFAULT_DAYS - 1)));
  // Only `undefined` for a defaulted day, which is always valid.
  const since = window.since ?? dayBoundary(from, "start");
  if (since === undefined) return { ok: false, problem: `The start date "${from}" is not a valid day.` };

  return {
    ok: true,
    filters: { since, ...(window.until !== undefined && { until: window.until }) },
    days: { from, ...(to !== undefined && { to }) },
  };
}
